import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { IoCalendarClearOutline } from 'react-icons/io5';
import { TbClockPlay } from 'react-icons/tb';

import { formatTime } from '../../utils';
import { setSelectedText } from '../../features/video/videoSlice';
import { Badge } from '../ui/Badge';

const renderHighlight = (text) => {
  const parts = String(text).split(/(<strong>[\s\S]*?<\/strong>)/gi);
  return parts.map((part, i) => {
    const match = /^<strong>([\s\S]*?)<\/strong>$/i.exec(part);
    if (match) {
      return (
        <span key={i} className="bg-yellow-200 font-semibold text-slate-900">
          {match[1]}
        </span>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

const SearchTable = ({ searchList }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useParams();

  const handleHighlightClick = (videoID, text) => {
    dispatch(setSelectedText(text.replace(/<\/?strong>/gi, '')));
    navigate(`/${state}/video/${videoID}`);
  };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-left">
            {['Date', 'Title', 'Chamber', 'Legislature', 'Duration'].map((h) => (
              <th
                key={h}
                className="px-4 py-3 font-medium uppercase tracking-wide text-xs text-slate-500 whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {searchList.length > 0 &&
            searchList.map(({ video, highlight }) => (
              <React.Fragment key={video.VideoId}>
                <tr className="bg-white transition-colors duration-150 hover:bg-slate-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-2 text-slate-700">
                      <IoCalendarClearOutline className="h-4 w-4 text-slate-400" />
                      {new Date(video.Date).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">{video.Committee}</td>
                  <td className="px-4 py-3 text-slate-700">
                    {video.Chamber === 'H' ? 'House' : 'Senate'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="secondary">{video.Legislature || '\u2014'}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-slate-700">
                      <TbClockPlay className="h-4 w-4 text-slate-400" />
                      {formatTime(video.Duration)}
                    </span>
                  </td>
                </tr>
                {highlight?.transcript?.length > 0 && (
                  <tr>
                    <td colSpan="5">
                      <div className="px-4 py-3 bg-slate-50 flex flex-col gap-1.5 border-t border-slate-100">
                        {highlight.transcript.map((text, i) => (
                          <button
                            key={i}
                            type="button"
                            className="text-left text-sm text-slate-600 hover:text-blue-600 hover:underline transition-colors"
                            onClick={() => handleHighlightClick(video.VideoId, text)}
                          >
                            {'\u2026'}
                            {renderHighlight(text)}
                            {'\u2026'}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
        </tbody>
      </table>
    </div>
  );
};

export default SearchTable;
